import { FC, Fragment, useEffect } from 'react';
import { FlightSearch, FlightSearchResponse } from 'ts/flight/interfaces/FlightSearch';
import { FlightFilterModel } from 'ts/flight/classes/FlightFilterModel';
import { FlightResultsTable } from './FlightResultsTable';
import FlightResultListSkeleton from '../Result/FlightResultListSkeleton';
import { SearchFlightModel } from 'ts/flight/classes/SearchFlightModel';
import { getBounds } from 'utils/flight/flight';
import { getDurationFromTime } from 'utils/utils';
import { FlightResult } from 'ts/flight/interfaces/FlightSearch';
import FlightPairsFilter from './FlightPairsFilter';
import FlightPairsTabs from './FlightPairsTabs';

interface FlightPairsResultProps {
  searchParams: SearchFlightModel;
  abortController: AbortController;
  sessionStorageKey: string;
  onRowClick: (flight?: FlightSearch) => void;
  selectedFlight?: FlightSearch;
  shortMode?: boolean;
  response?: FlightSearchResponse;
  flights?: FlightSearch[];
  setFlights: (flights?: FlightSearch[]) => void;
  soloMode?: boolean;
  pollingFinished?: boolean;
  filter?: FlightFilterModel;
  setFilter: (filter: FlightFilterModel) => void;
}

const FlightPairsResult: FC<FlightPairsResultProps> = ({
  flights,
  response,
  setFlights,
  filter,
  setFilter,
  ...props
}) => {
  useEffect(() => {
    if (flights && !props.selectedFlight) {
      props.onRowClick(flights[0]);
    }
  }, [flights?.length]); //eslint-disable-line

  const onClickResetFilter = () => {
    const newFilter = new FlightFilterModel(flights!);
    setFilter(newFilter);
  };

  const convertToTableData = (flights: FlightSearch[]): FlightResult[] => {
    return flights.map((flight) => {
      const bounds = getBounds(flight);

      const boundsFormatted = bounds.map((bound, boundIndex) => {
        const firstAir = bound.gdsFlyAirs?.[0];
        const fareFamilyDesc =
          bound.gdsFlyAirs?.find((air: any) => air.ffn?.trim())?.ffn.trim() ||
          flight.fareFamilyInfo?.[boundIndex]?.desc ||
          '';

        return {
          airlineCode: (
            <span className="text-bold">{`${bound.airline.code} ${bound.flightNumber}`}</span>
          ),
          remark: bound.remark,
          flightNumber: bound.flightNumber,
          departureDateTime: bound.departureDateTime,
          arrivalDateTime: bound.arrivalDateTime,
          departureAirport: {
            code: bound.departureAirport.code,
            desc: bound.departureAirport.desc,
          },
          arrivalAirport: {
            code: bound.arrivalAirport.code,
            desc: bound.arrivalAirport.desc,
          },
          durationTime: bound.elapsedDTime
            ? getDurationFromTime(bound.elapsedDTime * 60 * 1000, false, false)
            : '',
          availSeatsNo: bound.availSeatsNo || firstAir?.availSeatsNo || 0,
          fareFamilyDesc,
          cabin: {
            code: bound.cabin.code,
            desc: bound.cabin.desc,
          },
          class: {
            code: firstAir?.class?.code || bound.class.code,
            desc: firstAir?.class?.desc || bound.class.desc,
          },
          legCount: bound.legCount,
          stopQuantity: bound.legCount - 1,
          connTime: bound.maxConnDTime
            ? getDurationFromTime(bound.maxConnDTime * 60 * 1000, false, false)
            : '',
          stopAirports:
            bound.gdsFlyAirs?.slice(1).map((stop: any) => stop.departureAirport.code) || [],
          listOfFlyBaggagePrice: bound.listOfFlyBaggagePrice,
        };
      });

      return {
        id: flight.id,
        airlineDesc: flight.airlineDesc,
        flightSource: flight.flightSource,
        fareFamilyInfo: flight.fareFamilyInfo,
        bounds: boundsFormatted,
        priceGross: flight.flighPrice?.grossAndTaxWithMarkup?.value || 0,
        flightSFM: flight.flightSFM,
        flightPricesByPDT: flight.flightPricesByPDT,
        provider: flight.provider,
      };
    });
  };

  return (
    <Fragment>
      {!!flights?.length && (
        <div className={'fl-pair-subwrapper' + (props.soloMode ? ' fl-pair-solo' : '')}>
          <div className={`fl-pair ${props.shortMode ? 'short' : ''}`}>
            {!!filter && (
              <FlightPairsFilter
                filter={filter}
                setFilter={setFilter}
                onClickReset={() => onClickResetFilter()}
                flightsLength={flights.length}
                flightsVisibleLength={flights.filter((flight) => flight.visible).length}
              />
            )}

            <FlightResultsTable
              shortTable={props.shortMode}
              data={convertToTableData(flights.filter((flight) => flight.visible))}
              onRowClick={(flightId) => {
                const flight = flights.find((flight) => flight.id === flightId);
                props.onRowClick(flight);
              }}
              selectedFlId={props.selectedFlight?.id}
            />
          </div>
          {props.shortMode && props.selectedFlight && (
            <div
              className="flex-one display-flex fl-pair-details-wr"
              style={{
                marginTop: props.sessionStorageKey.includes('Inbound') ? -25 : 0,
              }}
            >
              <FlightPairsTabs
                flight={props.selectedFlight}
                sessionStorageKey={props.sessionStorageKey}
                soloMode={props.soloMode}
              />
            </div>
          )}
        </div>
      )}
      {!flights?.length && !props.pollingFinished && !response?.sessionExpired && (
        <FlightResultListSkeleton />
      )}
    </Fragment>
  );
};

export default FlightPairsResult;
